import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { gymApi, GymNotFoundError } from '@/lib/api';
import { Gym, Client } from '@/types';
import { reportErrorWithContext, setPageArea, setGymTags, clearGymTags } from '@/lib/sentry';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, CheckCircle2, XCircle, LogOut, User, Phone, QrCode, ChevronDown, Download, AlertTriangle, AlertCircle } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { ClientRewardsDialog } from '@/components/ClientRewardsDialog';
import { getOrCreateDeviceId, getCurrentCoordinates } from '@/lib/device-utils';

const STORAGE_KEY = 'gympro_self_checkin_auth';

const SelfCheckInPage = () => {
    const { slug } = useParams<{ slug: string }>();
    const { toast } = useToast();
    const [gym, setGym] = useState<Gym | null>(null);
    const [client, setClient] = useState<Client | null>(null);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [identifier, setIdentifier] = useState('');
    const [lastCheckIn, setLastCheckIn] = useState<any>(null);
    const [isQrExpanded, setIsQrExpanded] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [rewardsRefreshToken, setRewardsRefreshToken] = useState(0);
    const [checkInPromptSeq, setCheckInPromptSeq] = useState(0);
    const [processingHint, setProcessingHint] = useState('');
    
    useEffect(() => {
        setPageArea("scanner");
    }, []);

    useEffect(() => {
        if (!gym) return;
        setGymTags({ gym_id: gym.gym_id, name: gym.name, slug: gym.slug });
        return () => clearGymTags();
    }, [gym]);

    useEffect(() => {
        loadGymAndIdentity();
    }, [slug]);

    const loadGymAndIdentity = async () => {
        if (!slug) return;
        setLoading(true);
        let gymData = null;
        try {
            const gRes = await gymApi.getGymBySlug(slug);
            gymData = gRes.gym;
            setGym(gymData);
        } catch (error) {
            console.error('Error loading gym:', error);

            const isNetworkError =
                !(error instanceof GymNotFoundError) &&
                (error instanceof TypeError ||
                    /load failed|failed to fetch|networkerror|network error|fetch/i.test(
                        (error as Error)?.message || ''
                    ));

            const contextData: Record<string, unknown> = {
                slug: slug,
                url: window.location.href,
                userAgent: navigator.userAgent
            };

            const savedIdentity = localStorage.getItem(`${STORAGE_KEY}_${slug}`);
            if (savedIdentity) {
                contextData.cached_identifier_prefix = savedIdentity.slice(0, 3);
            }

            reportErrorWithContext(error, {
                operation: "self_checkin_load_gym",
                area: "scanner",
                contextName: "gym_lookup",
                contextData,
                extra: {
                    slug_attempted: slug,
                    error_type: isNetworkError ? "network_error" : "gym_not_found"
                }
            });

            toast({
                variant: "destructive",
                title: "Error",
                description: isNetworkError
                    ? "No se pudo conectar. Revisa tu conexión a internet e intenta de nuevo."
                    : "No se pudo cargar la información del gimnasio.",
            });
            setLoading(false);
            return;
        }

        // Intentar restaurar identidad guardada (errores aquí no afectan la carga del gym)
        const savedIdentity = localStorage.getItem(`${STORAGE_KEY}_${slug}`);
        if (savedIdentity && gymData) {
            try {
                const { clients } = await gymApi.findClientByIdentifier(gymData.gym_id, savedIdentity);
                if (clients && clients.length > 0) {
                    const matchedClient = clients.find(c => c.gym_id === gymData!.gym_id);
                    if (matchedClient) {
                        setClient(matchedClient);
                    } else {
                        localStorage.removeItem(`${STORAGE_KEY}_${slug}`);
                    }
                }
            } catch (identityError) {
                // Si falla la búsqueda de identidad en cache, simplemente se limpia
                // sin mostrar error al usuario (el gym ya cargó bien)
                console.warn('Could not restore cached identity, clearing:', identityError);
                localStorage.removeItem(`${STORAGE_KEY}_${slug}`);
            }
        }

        setLoading(false);
    };

    const handleIdentify = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!identifier.trim() || !gym) return;

        setProcessing(true);
        try {
            const { clients } = await gymApi.findClientByIdentifier(gym.gym_id, identifier.trim());

            const matchedClient = clients?.find(c => c.gym_id === gym.gym_id);

            if (matchedClient) {
                setClient(matchedClient);
                localStorage.setItem(`${STORAGE_KEY}_${slug}`, identifier.trim());
                toast({
                    title: "Identidad confirmada",
                    description: `¡Hola ${matchedClient?.name || 'Socio'}! Te recordaremos en este dispositivo.`,
                });
            } else {
                toast({
                    variant: "destructive",
                    title: "No encontrado",
                    description: "No pudimos encontrar un socio con ese dato en este gimnasio.",
                });
            }
        } catch (error) {
            reportErrorWithContext(error, {
                operation: "self_checkin_identify",
                area: "scanner",
                contextName: "identify_attempt",
                contextData: {
                    slug,
                    identifier_prefix: identifier.trim().slice(0, 3),
                },
            });
            toast({
                variant: "destructive",
                title: "Error",
                description: "Ocurrió un error al buscar tu cuenta.",
            });
        } finally {
            setProcessing(false);
        }
    };

    const handleCheckIn = async () => {
        if (!client || !gym || processing) return; // Prevention of multiple clicks
        setProcessing(true);
        setLastCheckIn(null);
        setProcessingHint('');

        try {
            let latitude: number | null = null;
            let longitude: number | null = null;

            // Geofence: the gym requires the member's real location.
            // Server-side validation is authoritative; this is just the client UX gate.
            const geofenceRequired =
                !!gym.settings?.geofence_enabled &&
                gym.latitude != null &&
                gym.longitude != null;

            if (geofenceRequired) {
                setProcessingHint('Verificando ubicación…');
                try {
                    const coords = await getCurrentCoordinates();
                    latitude = coords.latitude;
                    longitude = coords.longitude;
                } catch (geoError: any) {
                    const geoCode = geoError?.code;
                    const geoMessage =
                        geoCode === 1
                            ? 'Debes permitir el acceso a tu ubicación en el navegador para registrar tu asistencia.'
                            : geoCode === 3
                                ? 'No pudimos obtener tu ubicación a tiempo. Verifica tu GPS e intenta de nuevo.'
                                : geoError?.message === 'NO_GEOLOCATION'
                                    ? 'Tu dispositivo no permite obtener la ubicación. Pide al staff que registre tu asistencia.'
                                    : 'No pudimos obtener tu ubicación. Activa el GPS e intenta de nuevo.';

                    setLastCheckIn({ success: false, result: 'denied', message: geoMessage });
                    toast({
                        variant: "destructive",
                        title: "Ubicación requerida",
                        description: geoMessage,
                    });
                    return;
                }
            }

            setProcessingHint('Registrando asistencia…');
            const res = await gymApi.selfCheckIn(
                client.client_id,
                gym.gym_id,
                'self_checkin',
                getOrCreateDeviceId(),
                latitude,
                longitude
            );
            setLastCheckIn(res);

            if (res.result === 'allowed') {
                toast({
                    title: "¡Éxito!",
                    description: res.message || "Asistencia registrada correctamente.",
                });
                setRewardsRefreshToken(t => t + 1);
                setCheckInPromptSeq(s => s + 1);
            } else {
                toast({
                    variant: "destructive",
                    title: "Acceso denegado",
                    description: res.message || "Tu membresía no permite el acceso.",
                });
            }
        } catch (error) {
            reportErrorWithContext(error, {
                operation: "self_checkin_submit",
                area: "scanner",
                contextName: "checkin_attempt",
                contextData: {
                    gym_id: gym.gym_id,
                    client_id: client.client_id,
                },
            });
            toast({
                variant: "destructive",
                title: "Error",
                description: "No se pudo registrar la asistencia.",
            });
        } finally {
            setProcessing(false);
            setProcessingHint('');
        }
    };

    const handleLogout = () => {
        if (!slug) return;
        localStorage.removeItem(`${STORAGE_KEY}_${slug}`);
        setClient(null);
        setIdentifier('');
        setLastCheckIn(null);
        setIsQrExpanded(false);
    };

    const membershipDaysLeft = client?.current_end_date
        ? differenceInCalendarDays(parseISO(client.current_end_date), new Date())
        : null;
    const hasActiveMembership =
        membershipDaysLeft !== null &&
        membershipDaysLeft >= 0 &&
        (client?.current_sessions === null ||
            client?.current_sessions === undefined ||
            client.current_sessions > 0);

    const handleDownloadQR = async () => {
        if (!client) return;
        setIsDownloading(true);
        try {
            const qrUrl = `https://quickchart.io/qr?text=${encodeURIComponent(client.qr_code)}&size=400`;
            const response = await fetch(qrUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `QR_${client.name.replace(/\s+/g, '_')}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast({
                title: "QR Descargado",
                description: "Tu código de acceso se ha guardado en tu dispositivo.",
            });
        } catch (error) {
            console.error('Error downloading QR:', error);
            reportErrorWithContext(error, {
                operation: "self_checkin_qr_download",
                area: "scanner",
                contextName: "qr_download",
                contextData: { client_id: client.client_id },
            });
            toast({
                variant: "destructive",
                title: "Error",
                description: "No se pudo descargar el código QR.",
            });
        } finally {
            setIsDownloading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-black text-white p-4">
                <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
                <p className="text-lg font-medium opacity-70">Cargando...</p>
            </div>
        );
    }

    if (!gym) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-black text-white p-4 text-center">
                <XCircle className="h-16 w-16 text-destructive mb-4" />
                <h1 className="text-2xl font-bold mb-2">Gimnasio no encontrado</h1>
                <p className="opacity-70">El enlace que has seguido no es válido.</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-black text-white transition-all duration-500 overflow-x-hidden">
            {/* Premium Background Background Effects */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-primary/20 rounded-full blur-[120px] animate-pulse" />
                <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-orange-600/10 rounded-full blur-[120px]" />
            </div>

            <div className="relative z-10 max-w-md mx-auto min-h-screen flex flex-col p-6">
                {/* Header */}
                <div className="flex flex-col items-center text-center mt-8 mb-12">
                    {gym.logo ? (
                        <img src={gym.logo} alt={gym.name} className="h-20 w-auto mb-4 object-contain" />
                    ) : (
                        <div className="h-16 w-16 bg-gradient-to-br from-primary to-orange-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg shadow-primary/20">
                            <span className="text-2xl font-bold">{gym.name.substring(0, 2).toUpperCase()}</span>
                        </div>
                    )}
                    <h1 className="text-3xl font-black tracking-tighter uppercase italic text-white leading-tight">
                        {gym.name}
                    </h1>
                    <p className="text-sm font-bold text-primary/80 tracking-widest uppercase mt-2">
                        Auto Check-in
                    </p>
                </div>

                {!client ? (
                    <Card className="bg-zinc-900/50 border-zinc-800 backdrop-blur-xl rounded-[2rem] shadow-2xl border-2">
                        <CardHeader>
                            <CardTitle className="text-2xl font-black text-white text-center uppercase italic tracking-tight">Identificarse</CardTitle>
                            <CardDescription className="text-center text-zinc-400 font-medium">
                                Ingresa tus datos una sola vez para registrar tu asistencia.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleIdentify} className="space-y-4">
                                <div className="relative group">
                                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                        <Phone className="h-5 w-5 text-primary opacity-50 group-focus-within:opacity-100 transition-opacity" />
                                    </div>
                                    <Input
                                        placeholder="Teléfono o ID de Socio"
                                        value={identifier}
                                        onChange={(e) => setIdentifier(e.target.value)}
                                        className="bg-zinc-950 border-zinc-800 h-14 pl-12 rounded-2xl text-lg font-bold focus:border-primary transition-all text-white placeholder:text-zinc-500"
                                        disabled={processing}
                                        required
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    disabled={processing}
                                    className="w-full h-14 rounded-2xl bg-primary hover:bg-primary/90 text-white font-black text-lg shadow-xl shadow-primary/20 transition-all active:scale-[0.98]"
                                >
                                    {processing ? <Loader2 className="animate-spin" /> : "CONTINUAR"}
                                </Button>
                            </form>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-6 flex-1 flex flex-col justify-center animate-in fade-in zoom-in duration-500">
                        <div className="text-center space-y-2">
                            <div className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-zinc-900 border-2 border-primary/50 mb-4">
                                <User className="h-10 w-10 text-primary" />
                            </div>
                            <h2 className="text-3xl font-black text-white italic">¡HOLA, {(client?.name || 'SOCIO').toUpperCase()}!</h2>
                            <p className="text-zinc-400 font-medium">¿Listo para entrenar hoy?</p>
                        </div>

                        {/* Membership Expiry Info */}
                        {client.current_end_date && (
                            <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-4 text-center">
                                <p className="text-[10px] text-zinc-500 font-black uppercase tracking-[0.2em] mb-1">Vencimiento de Membresía</p>
                                <p className="text-lg font-bold text-white">
                                    {(() => {
                                        const endDate = parseISO(client.current_end_date);
                                        const diffDays = differenceInCalendarDays(endDate, new Date());
                                        const formatted = endDate.toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' });
                                        if (diffDays < 0) {
                                            const pastDays = Math.abs(diffDays);
                                            return <span className="text-red-400">Venció hace {pastDays} {pastDays === 1 ? 'día' : 'días'} — {formatted}</span>;
                                        }
                                        if (diffDays === 0) {
                                            return <span className="text-orange-400">Vence hoy — {formatted}</span>;
                                        }
                                        if (diffDays <= 3) {
                                            return <span className="text-orange-400">Vence en {diffDays} {diffDays === 1 ? 'día' : 'días'} — {formatted}</span>;
                                        }
                                        return <>{formatted}</>;
                                    })()}
                                </p>
                            </div>
                        )}

                        {/* PREVENTION ALERT BANNER (Fase 3A) */}
                        {client && (() => {
                            const diffDays = client.current_end_date
                                ? differenceInCalendarDays(parseISO(client.current_end_date), new Date())
                                : null;
                            const lowSessions = client.current_sessions !== null && client.current_sessions !== undefined && client.current_sessions <= 2;

                            if (diffDays !== null && diffDays < 0) {
                                const pastDays = Math.abs(diffDays);
                                return (
                                    <div className="bg-red-500/20 border-2 border-red-500/50 rounded-2xl p-4 flex items-center gap-4">
                                        <div className="bg-red-500 rounded-full p-2">
                                            <AlertCircle className="h-6 w-6 text-white" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-black text-white uppercase italic tracking-tight">Membresía Vencida</p>
                                            <p className="text-xs text-red-200 font-bold">
                                                Tu plan venció hace {pastDays} {pastDays === 1 ? 'día' : 'días'}. Renueva en recepción para seguir entrenando.
                                            </p>
                                        </div>
                                    </div>
                                );
                            }

                            if (diffDays !== null && diffDays <= 3) {
                                return (
                                    <div className="bg-orange-500/20 border-2 border-orange-500/50 rounded-2xl p-4 flex items-center gap-4 animate-pulse">
                                        <div className="bg-orange-500 rounded-full p-2">
                                            <AlertCircle className="h-6 w-6 text-white" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-black text-white uppercase italic tracking-tight">Recordatorio de Renovación</p>
                                            <p className="text-xs text-orange-200 font-bold">
                                                Tu plan vence en {diffDays === 0 ? 'hoy' : `${diffDays} ${diffDays === 1 ? 'día' : 'días'}`}.
                                            </p>
                                        </div>
                                    </div>
                                );
                            }

                            if (lowSessions) {
                                return (
                                    <div className="bg-orange-500/20 border-2 border-orange-500/50 rounded-2xl p-4 flex items-center gap-4 animate-pulse">
                                        <div className="bg-orange-500 rounded-full p-2">
                                            <AlertCircle className="h-6 w-6 text-white" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-sm font-black text-white uppercase italic tracking-tight">Recordatorio de Renovación</p>
                                            <p className="text-xs text-orange-200 font-bold">
                                                Te quedan solo {client.current_sessions} {client.current_sessions === 1 ? 'sesión' : 'sesiones'}.
                                            </p>
                                        </div>
                                    </div>
                                );
                            }

                            return null;
                        })()}

                        <div className="relative group">
                            <div className="absolute -inset-4 bg-gradient-to-r from-primary to-orange-600 rounded-[3rem] blur-xl opacity-20 group-hover:opacity-40 animate-pulse duration-[3000ms]"></div>
                            <Button
                                onClick={handleCheckIn}
                                disabled={processing}
                                className={cn(
                                    "relative w-full h-44 rounded-[2.5rem] flex flex-col items-center justify-center gap-4 transition-all duration-300 active:scale-95 overflow-hidden border-4",
                                    !lastCheckIn && "bg-gradient-to-br from-primary via-orange-500 to-orange-600 border-white/20 shadow-2xl shadow-primary/40",
                                    lastCheckIn?.result === 'allowed' && "bg-green-600 border-green-400 shadow-2xl shadow-green-500/40",
                                    lastCheckIn?.result === 'denied' && "bg-red-600 border-red-400 shadow-2xl shadow-red-500/40"
                                )}
                            >
                                <div className="absolute inset-0 bg-white/10 opacity-0 group-active:opacity-100 transition-opacity"></div>

                                {processing ? (
                                    <Loader2 className="h-16 w-16 animate-spin text-white" />
                                ) : (
                                    <>
                                        {lastCheckIn ? (
                                            lastCheckIn.result === 'allowed' ? (
                                                <CheckCircle2 className="h-16 w-16 text-white animate-bounce" />
                                            ) : (
                                                <XCircle className="h-16 w-16 text-white" />
                                            )
                                        ) : (
                                            <div className="bg-white/20 p-4 rounded-full">
                                                <CheckCircle2 className="h-12 w-12 text-white group-hover:scale-110 transition-transform duration-300" />
                                            </div>
                                        )}
                                        <div className="flex flex-col items-center">
                                            <span className="font-black text-2xl md:text-3xl tracking-tighter text-white drop-shadow-md">
                                                {lastCheckIn ? (lastCheckIn.result === 'allowed' ? "ACCESO CONCEDIDO" : "ACCESO DENEGADO") : "MARCAR ASISTENCIA"}
                                            </span>
                                            {!lastCheckIn && <span className="text-[10px] font-bold text-white/70 uppercase tracking-[0.3em] mt-1">Presiona aquí para entrar</span>}
                                        </div>
                                    </>
                                )}
                            </Button>
                        </div>

                        {processing && processingHint && (
                            <p className="text-center text-xs font-bold uppercase tracking-[0.2em] text-primary/80 animate-pulse">
                                {processingHint}
                            </p>
                        )}

                        {lastCheckIn && (
                            <div className="space-y-4 animate-in slide-in-from-top-4">
                                <div className={cn(
                                    "p-4 rounded-2xl text-center font-bold",
                                    lastCheckIn.result === 'allowed' ? "bg-green-500/10 text-green-400 border border-green-500/20" : "bg-red-500/10 text-red-400 border border-red-500/20"
                                )}>
                                    {lastCheckIn.message}
                                </div>
                                {lastCheckIn.result === 'allowed' && (
                                    <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-4 text-center space-y-2">
                                        <p className="text-[10px] text-zinc-500 font-black uppercase tracking-[0.2em] mb-1">Estado de Membresía</p>
                                        {lastCheckIn.sessions_remaining !== undefined && lastCheckIn.sessions_remaining !== null && (
                                            <p className="text-lg font-bold text-white">
                                                {lastCheckIn.sessions_remaining === 0 
                                                  ? "⚠️ ÚLTIMA SESIÓN CONSUMIDA" 
                                                  : `${lastCheckIn.sessions_remaining} SESIONES RESTANTES`}
                                            </p>
                                        )}
                                        {client.current_end_date && (
                                            <p className="text-sm font-medium text-zinc-400">
                                                Vencimiento: {parseISO(client.current_end_date).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="pt-8 space-y-4">
                            <Collapsible
                                open={isQrExpanded}
                                onOpenChange={setIsQrExpanded}
                                className="w-full bg-zinc-900/30 border border-zinc-800 rounded-2xl overflow-hidden"
                            >
                                <CollapsibleTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        className="w-full h-12 flex justify-between px-4 hover:bg-zinc-800/50 text-zinc-400 font-bold"
                                    >
                                        <div className="flex items-center gap-2">
                                            <QrCode className="h-4 w-4" />
                                            <span>MI CÓDIGO QR</span>
                                        </div>
                                        <ChevronDown className={cn("h-4 w-4 transition-transform duration-300", isQrExpanded && "rotate-180")} />
                                    </Button>
                                </CollapsibleTrigger>
                                <CollapsibleContent className="p-6 flex flex-col items-center gap-6 animate-in slide-in-from-top-2 duration-300">
                                    <div className="bg-white p-4 rounded-3xl shadow-2xl shadow-white/5">
                                        <img
                                            src={`https://quickchart.io/qr?text=${encodeURIComponent(client.qr_code)}&size=200`}
                                            alt={`QR ${client.name}`}
                                            className="w-40 h-40"
                                        />
                                    </div>
                                    <div className="text-center space-y-1">
                                        <p className="text-[10px] text-zinc-500 font-black tracking-widest uppercase">Tu Llave Digital</p>
                                        <p className="text-xs text-zinc-400 font-medium max-w-[200px]">Usa este código para entrar sin tener que escribir tus datos.</p>
                                    </div>
                                    <Button
                                        onClick={handleDownloadQR}
                                        disabled={isDownloading}
                                        variant="outline"
                                        className="w-full h-12 rounded-xl border-primary/30 text-primary hover:bg-primary/10 font-bold"
                                    >
                                        {isDownloading ? (
                                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                        ) : (
                                            <Download className="h-4 w-4 mr-2" />
                                        )}
                                        {isDownloading ? "DESCARGANDO..." : "DESCARGAR QR"}
                                    </Button>
                                </CollapsibleContent>
                            </Collapsible>

                            <Button
                                variant="ghost"
                                onClick={handleLogout}
                                className="w-full text-zinc-500 hover:text-white hover:bg-zinc-900 rounded-xl"
                            >
                                <LogOut className="h-4 w-4 mr-2" />
                                No soy {client?.name || 'tú'}
                            </Button>
                        </div>
                    </div>
                )}

                {client && gym && hasActiveMembership && lastCheckIn?.result === 'allowed' && checkInPromptSeq > 0 && (
                    <ClientRewardsDialog
                        key={`${gym.gym_id}_${client.client_id}_${checkInPromptSeq}`}
                        gymId={gym.gym_id}
                        clientId={client.client_id}
                        clientName={client.name}
                        storageKey={`${STORAGE_KEY}_rewards_${slug}_${client.client_id}`}
                        refreshToken={rewardsRefreshToken}
                    />
                )}

                {/* Footer info */}
                <div className="mt-auto pt-8 pb-4 text-center">
                    <p className="text-[10px] text-zinc-600 font-bold tracking-[0.2em] uppercase">
                        Powered by GymPro SV
                    </p>
                </div>
            </div>
        </div>
    );
};

export default SelfCheckInPage;
